import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { shellEventBus } from '../services/shellBridge';

/**
 * Broadcasts PSP navigation changes to the shell event bus so that globally-mounted
 * ES modules stay in sync regardless of which route-based app is currently active.
 *
 * Uses React Router's useLocation() as the source of truth — this catches every
 * navigation React Router drives (sidebar links, useNavigate, redirects, back/forward)
 * without relying on pushState interception, which React Router v6 may bypass
 * when scheduling navigation updates through startTransition.
 *
 * Skips the initial mount to avoid emitting on page load.
 */
export function useNavigationBroadcast(): void {
  const location = useLocation();
  const isMountRef = useRef(true);

  // Intentional use of useEffect (exception to CLAUDE.md rule):
  //   - Cannot derive state: emission is a side effect, not a value.
  //   - Cannot use a data-fetching hook: this isn't a request.
  //   - Cannot emit from event handlers: React Router v6 can schedule
  //     navigation via startTransition (bypassing pushState interception),
  //     and back/forward buttons + redirect routes emit no click event.
  //   - useMountEffect doesn't apply: we must react to every navigation,
  //     not run once.
  //
  // Depend on the location OBJECT, not the pathname string. React Router gives
  // a new location (new `key`) for every navigation — including pushes to the
  // path PSP already perceives. That case is real: a remote app's nested router
  // navigates without PSP observing it, leaving PSP's perceived path stale, so
  // re-selecting that app from the PSP sidebar resolves to the same pathname
  // string. Keying off `pathname` would silently drop that navigation (PSS-2647).
  useEffect(() => {
    if (isMountRef.current) {
      isMountRef.current = false;
      return;
    }
    shellEventBus.emit('navigationStart', location.pathname);
  }, [location]);
}
